import type {Edge, Node, NodeHandle} from "@xyflow/react";
import {Position} from "@xyflow/react";
import {hierarchy, tree as d3tree} from "d3-hierarchy";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import type {ChangeKind, ChangeStep} from "~/prototypes/pages/taxonomy-canvas/approval/changeSteps.ts";
import {indexTree} from "~/prototypes/pages/taxonomy-canvas/approval/changeSteps.ts";

export const DIFF_NODE_WIDTH = 190;
export const DIFF_NODE_HEIGHT = 50;
/** Sub-categories shown under a changed category; the rest is summarised as "+N more" */
const MAX_CHILDREN = 5;
/** Sub-categories shown under a merge result (it holds the children of both) */
const MAX_MERGED_CHILDREN = 8;

export type DiffNodeData = {
    /** Current name */
    label: string
    /** Name before the change, when it was renamed */
    prevLabel?: string
    level: number
    /**
     * focus: the changed category (colour); context: unchanged, for orientation;
     * previous: a category as it was before (merge originals and their sub-categories, dashed);
     * result: a sub-category of a merge result (colour); more: "+N more"
     */
    role: 'focus' | 'context' | 'previous' | 'result' | 'more'
    /** Deleted / merged away (itself or an ancestor): shown faded */
    removed: boolean
    kind: ChangeKind
    note?: string
}
export type DiffNode = Node<DiffNodeData, 'diff'>

const HANDLES: NodeHandle[] = [
    {type: 'target', position: Position.Left, x: 0, y: DIFF_NODE_HEIGHT / 2},
    {type: 'source', position: Position.Right, x: DIFF_NODE_WIDTH, y: DIFF_NODE_HEIGHT / 2},
];

type Item = {id: string, data: DiffNodeData, children: Item[]}

/**
 * One picture of a change, before and after combined, laid out as the taxonomy is *after* the change:
 * - solid edges: the current structure; dashed edges: where a category hung before (old parent → category)
 * - deleted / merged-away categories stay at their old place, faded, with dashed edges
 * - renamed categories carry their previous name
 * Shown: the path from the root to every changed / context category, plus up to MAX_CHILDREN sub-categories
 * of each changed category (the ones that came from elsewhere first).
 *
 * A merge is drawn differently: both original categories appear as dashed "previous" cards with their own
 * (dashed) sub-categories, next to the coloured merge result holding all the merged sub-categories.
 */
export function buildDiffGraph(step: ChangeStep): {nodes: DiffNode[], edges: Edge[], height: number} {
    const {before, after, kind} = step;
    const merge = step.edit.type === 'merge' && step.applies ? step.edit : undefined;
    const a = indexTree(after);
    const b = indexTree(before);
    const parentNow = (id: number) => a.get(id)?.parent?.id;
    const parentBefore = (id: number) => b.get(id)?.parent?.id;
    const cameFromElsewhere = (id: number) => b.has(id) && parentBefore(id) !== parentNow(id);

    const keep = new Set<number>();
    const keepPath = (id: number | undefined) => {
        for (let e = id === undefined ? undefined : a.get(id); e; e = e.parent ? a.get(e.parent.id) : undefined) keep.add(e.node.id);
    };
    const focus = new Set(step.focusIds.filter(id => a.has(id)));
    [...focus, ...step.anchorIds].forEach(keepPath);

    // Visible sub-categories of each changed category, plus where the moved ones came from
    const visibleChildren = new Map<number, {shown: m_taxonomy.Tree[], hidden: number}>();
    for (const id of focus) {
        const node = a.get(id)!.node;
        const candidates = node.children.filter(c => node.removed || !c.removed || focus.has(c.id));
        const ordered = [...candidates.filter(c => focus.has(c.id) || keep.has(c.id) || cameFromElsewhere(c.id)),
            ...candidates.filter(c => !focus.has(c.id) && !keep.has(c.id) && !cameFromElsewhere(c.id))];
        const max = merge?.targetId === id ? MAX_MERGED_CHILDREN : MAX_CHILDREN;
        const shown = ordered.slice(0, Math.max(max, ordered.filter(c => focus.has(c.id) || keep.has(c.id)).length));
        visibleChildren.set(id, {shown, hidden: ordered.length - shown.length});
        shown.forEach(c => keep.add(c.id));
    }
    // Old parents of everything that moved must be on the picture to draw the dashed "was here" edge
    // (not for a merge: there the originals are drawn in full instead)
    if (!merge) {
        for (const id of [...keep]) {
            if (cameFromElsewhere(id)) keepPath(parentBefore(id));
        }
    }

    const moreItem = (id: string, hidden: number, level: number, removed: boolean): Item => ({
        id, data: {label: `+${hidden} more`, level, role: 'more', removed, kind}, children: [],
    });

    /** A merge original as it was before: dashed, with its own sub-categories (ids prefixed, they also appear in the result) */
    const previousItem = (n: m_taxonomy.Tree, level: number, withChildren: boolean, note?: string): Item => {
        const kids = withChildren ? n.children.filter(c => !c.removed) : [];
        const children = kids.slice(0, MAX_CHILDREN).map(c => previousItem(c, level + 1, false));
        if (kids.length > MAX_CHILDREN) children.push(moreItem(`prev-more-${n.id}`, kids.length - MAX_CHILDREN, level + 1, true));
        return {id: `prev-${n.id}`, data: {label: n.label, level, role: 'previous', removed: false, kind, note}, children};
    };

    const toItem = (n: m_taxonomy.Tree, level: number, removedAbove: boolean, inMergeResult = false): Item => {
        const removed = removedAbove || Boolean(n.removed);
        const isMergeResult = merge?.targetId === n.id;
        const fixed = visibleChildren.get(n.id);
        const children = (fixed ? fixed.shown : n.children.filter(c => keep.has(c.id))).flatMap(c => {
            if (merge && (c.id === merge.sourceId || c.id === merge.targetId)) {
                // The originals (from the tree before the merge) take the place of the merged-away category
                const original = b.get(c.id)?.node;
                const originalItem = original
                    ? [previousItem(original, level + 1, true, `merged into "${a.get(merge.targetId)?.node.label}"`)]
                    : [];
                return c.id === merge.targetId ? [...originalItem, toItem(c, level + 1, removed)] : originalItem;
            }
            return [toItem(c, level + 1, removed, isMergeResult)];
        });
        if (fixed && fixed.hidden > 0) children.push(moreItem(`more-${n.id}`, fixed.hidden, level + 1, removed));
        const prev = b.get(n.id)?.node.label;
        return {
            id: String(n.id),
            data: {
                label: n.label,
                // A merge result is a new category: its previous names are on the dashed originals
                prevLabel: !isMergeResult && prev !== undefined && prev !== n.label ? prev : undefined,
                level,
                role: focus.has(n.id) ? 'focus' : inMergeResult ? 'result' : 'context',
                removed,
                kind,
                note: isMergeResult ? 'merge result' : step.afterNotes.get(n.id),
            },
            children,
        };
    };

    // d3 lays out top-down; swap the axes for a left-to-right tree
    const h = d3tree<Item>().nodeSize([DIFF_NODE_HEIGHT + 14, DIFF_NODE_WIDTH + 36])(
        hierarchy(toItem(after, 0, false), d => d.children)
    );
    const nodes: DiffNode[] = h.descendants().map(d => ({
        id: d.data.id,
        type: 'diff',
        position: {x: d.y, y: d.x},
        width: DIFF_NODE_WIDTH,
        height: DIFF_NODE_HEIGHT,
        handles: HANDLES,
        data: d.data.data,
    }));
    const shown = new Set(nodes.map(n => n.id));
    const edges: Edge[] = h.links().map(l => ({
        id: `${l.source.data.id}-${l.target.data.id}`,
        source: l.source.data.id,
        target: l.target.data.id,
        type: 'smoothstep',
        // Edges into removed or "previous" categories are part of the previous state: dashed
        ...(l.target.data.data.removed || l.target.data.data.role === 'previous' ? {className: 'diff-edge-previous'} : {}),
    }));
    // Previous position of everything that moved: dashed edge from its old parent
    for (const id of keep) {
        const prevParent = parentBefore(id);
        if (merge || !cameFromElsewhere(id) || prevParent === undefined || !shown.has(String(prevParent))) continue;
        edges.push({
            id: `prev-${prevParent}-${id}`,
            source: String(prevParent),
            target: String(id),
            // A curve, so it doesn't run on top of the (right-angled) current edges into the same card
            type: 'default',
            className: 'diff-edge-previous diff-edge-moved',
        });
    }
    // Canvas height that fits the picture at (close to) full size
    const ys = nodes.map(n => n.position.y);
    const extent = nodes.length ? Math.max(...ys) - Math.min(...ys) + DIFF_NODE_HEIGHT : 0;
    const height = Math.min(600, Math.max(240, extent + 90));
    return {nodes, edges, height};
}
